/**
 *
 * Arquivo de helpers com funções auxiliares para formatações e validações.
 */

import { Phone } from "@mui/icons-material";

const MONTHS = [
  "",
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

const daysOfWeek = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const ufs = [
  { sigla: "AC", nome: "Acre" },
  { sigla: "AL", nome: "Alagoas" },
  { sigla: "AP", nome: "Amapá" },
  { sigla: "AM", nome: "Amazonas" },
  { sigla: "BA", nome: "Bahia" },
  { sigla: "CE", nome: "Ceará" },
  { sigla: "DF", nome: "Distrito Federal" },
  { sigla: "ES", nome: "Espírito Santo" },
  { sigla: "GO", nome: "Goiás" },
  { sigla: "MA", nome: "Maranhão" },
  { sigla: "MT", nome: "Mato Grosso" },
  { sigla: "MS", nome: "Mato Grosso do Sul" },
  { sigla: "MG", nome: "Minas Gerais" },
  { sigla: "PA", nome: "Pará" },
  { sigla: "PB", nome: "Paraíba" },
  { sigla: "PR", nome: "Paraná" },
  { sigla: "PE", nome: "Pernambuco" },
  { sigla: "PI", nome: "Piauí" },
  { sigla: "RJ", nome: "Rio de Janeiro" },
  { sigla: "RN", nome: "Rio Grande do Norte" },
  { sigla: "RS", nome: "Rio Grande do Sul" },
  { sigla: "RO", nome: "Rondônia" },
  { sigla: "RR", nome: "Roraima" },
  { sigla: "SC", nome: "Santa Catarina" },
  { sigla: "SP", nome: "São Paulo" },
  { sigla: "SE", nome: "Sergipe" },
  { sigla: "TO", nome: "Tocantins" },
];

const moneyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

// 1. validateEmail
function validateEmail(email: any) {
  return /^[\w+.]+@\w+\.\w{2,}(?:\.\w{2})?$/.test(email);
}

// 2. formatMoney
function formatMoney(value: any) {
  if (value === null || value === undefined || isNaN(value)) {
    return "R$ 0,00"; // Retorna um valor padrão se o valor for inválido
  }
  return moneyFormatter.format(value);
}

// 3. onlyNumbers
function onlyNumbers(value: any) {
  if (!value) {
    return "";
  }
  return value.replace(/\D/g, ""); // Remove tudo que não for número
}

// 4. formatPercent
function formatPercent(value: any) {
  if (value === null || value === undefined || isNaN(value)) {
    return "0%"; // Retorna um valor padrão se o valor for inválido
  }
  const symbol = value > 0 ? "+" : "";
  return symbol + value.toFixed(2).replace(".", ",") + "%";
}

// 5. getMonth
function getMonth(monthNumber: any) {
  if (monthNumber < 1 || monthNumber > 12) {
    return "Mês inválido"; // Retorna uma mensagem de erro se o mês for inválido
  }
  return MONTHS[monthNumber];
}

// 6. getPercent
function getPercent(current: any, previous: any) {
  if (previous === 0) {
    return current > 0 ? 100 : -100; // Evita divisão por zero
  }
  return (current / previous - 1) * 100;
}

// 7. formatDocument
const formatDocument = (value: string): string => {
  if (!value) {
    return "";
  }
  value = value.replace(/[^a-zA-Z0-9]/g, "");
  if (value.length <= 11) {
    // Formata CPF
    value = value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  } else {
    // Formata CNPJ
    value = value.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5",
    );
  }
  return value;
};

// 8. formatDocumentType
const formatDocumentType = (
  value: string | undefined,
  typeDoc?: string,
): string => {
  if (!value) {
    return "";
  }
  value = value + "";
  const digits = value.replace(/[^a-zA-Z0-9]/g, "");

  const docType = (typeDoc || (digits.length <= 11 ? "F" : "J")).toUpperCase();

  if (docType === "J") {
    // CNPJ: 00.000.000/0000-00
    let formatted = digits;
    if (formatted.length > 2)
      formatted = formatted.replace(/^(\w{2})(\w)/, "$1.$2");
    if (formatted.length > 6)
      formatted = formatted.replace(/^(\w{2})\.(\w{3})(\w)/, "$1.$2.$3");
    if (formatted.length > 9)
      formatted = formatted.replace(
        /^(\w{2})\.(\w{3})\.(\w{3})(\w)/,
        "$1.$2.$3/$4",
      );
    if (formatted.length > 13)
      formatted = formatted.replace(
        /^(\w{2})\.(\w{3})\.(\w{3})\/(\w{4})(\w)/,
        "$1.$2.$3/$4-$5",
      );
    return formatted;
  } else {
    // CPF: 000.000.000-00
    let formatted = digits;
    if (formatted.length > 3)
      formatted = formatted.replace(/^(\w{3})(\w)/, "$1.$2");
    if (formatted.length > 6)
      formatted = formatted.replace(/^(\w{3})\.(\w{3})(\w)/, "$1.$2.$3");
    if (formatted.length > 9)
      formatted = formatted.replace(
        /^(\w{3})\.(\w{3})\.(\w{3})(\w)/,
        "$1.$2.$3-$4",
      );
    return formatted;
  }
};

// 9. formatData
function formatData(input: any) {
  if (!input || input === "0000-00-00") return "";

  // String "DD/MM/YYYY"
  if (typeof input === "string") {
    const s = input.trim();

    // DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
      const [dd, mm, yyyy] = s.split("/");
      return `${dd}/${mm}/${yyyy}`;
    }

    // YYYY-MM-DD (ou ISO começando assim)
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      const [yyyy, mm, dd] = s.slice(0, 10).split("-");
      return `${dd}/${mm}/${yyyy}`;
    }
  }

  // Date ou timestamp numérico
  const d = input instanceof Date ? input : new Date(input as any);
  if (Number.isNaN(d.getTime())) return "";

  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

// 10. formatCEP
function formatCEP(CEPNumber: any) {
  if (!CEPNumber) {
    return "";
  }
  return CEPNumber.replace(/(\d{5})(\d{3})/g, "$1-$2");
}

// 11. formatPhone
function formatPhone(phoneNumber: string, typePhone?: string) {
  if (!phoneNumber) {
    return "";
  }
  phoneNumber = phoneNumber + "";
  const digits = phoneNumber.replace(/\D/g, "");
  if (typePhone === "C") {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  } else if (typePhone === "T") {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  } else {
    if (digits.length === 10) {
      return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
    } else if (digits.length === 11) {
      return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
    } else {
      return digits;
    }
  }
}

// 12. formatTrim
function formatTrim(trimString: any) {
  if (!trimString) {
    return "";
  }
  return trimString.trim();
}

// 13. formatUpper
function formatUpper(upperString: any) {
  if (!upperString) {
    return "";
  }
  return upperString.toUpperCase();
}

// 14. formatLower
function formatLower(lowerString: any) {
  if (!lowerString) {
    return "";
  }
  return lowerString.toLowerCase();
}

// 15. formatNumber
function formatNumber(numberToFormat: string) {
  if (!numberToFormat) {
    return "";
  }
  numberToFormat = numberToFormat + "";
  return numberToFormat.replace(/[^0-9,]*/g, "");
}

// 16. completeDoc
function completeDoc(doc: any, type: any) {
  if (!doc) {
    return "";
  }
  doc = doc + "";
  if (type === "F") {
    return doc.padStart(11, "0");
  } else {
    return doc.padStart(14, "0");
  }
}

// 17. getPhoneWithCountryCode
const getPhoneWithCountryCode = (phone: string) => {
  if (phone.startsWith("55") || phone.startsWith("5555")) {
    return phone;
  }
  return `55${phone}`;
};

// 18. removeTagHTML
function removeTagHTML(text: string) {
  if (!text) {
    return "";
  }
  return text.replace(/<[^>]*>?/gm, "");
}

// 19. validateDocument
function validateDocument(document: string, documentType?: string) {
  if (!document) {
    return false;
  }
  if (document) {
    document = document.replace(/[^a-zA-Z0-9]/g, "");
    if (documentType === undefined) {
      documentType = document.length <= 11 ? "F" : "J";
    }

    return documentType === "F" ? isValidCPF(document) : isValidCNPJ(document);
  }

  return false;
}

function isCelular(telefone: any): boolean {
  telefone = telefone + ""; // Garante que é string

  if (!telefone || typeof telefone !== "string") {
    return false;
  }

  // Remove todos os caracteres que não sejam números
  const apenasNumeros = telefone.replace(/\D/g, "");

  // Verifica se tem 11 dígitos (DDD + 9 + 8 dígitos do número)
  if (apenasNumeros.length !== 11) {
    return false;
  }

  // Impede sequências repetidas inválidas (ex: 11999999999 ou 00000000000)
  if (/^(\d)\1+$/.test(apenasNumeros)) {
    return false;
  }
  // Valida o formato: DDD (11-99) + Nono dígito (9) + Prefixo de celular (6, 7, 8 ou 9)
  const regexCelular = /^[1-9]{2}[6-9]{2}\d{7}$/;

  return regexCelular.test(apenasNumeros);
}

// 20. validNumberPhone
function validNumberPhone(phone: string): string | null {
  if (!phone) {
    return null;
  }
  const cleanedPhone = phone.replace(/\D/g, "");
  return cleanedPhone !== "0000000000" && cleanedPhone !== "00000000000"
    ? cleanedPhone
    : null;
}

// 21. formatVoucher
function formatVoucher(voucher: string) {
  if (!voucher) {
    return "";
  }
  // Remove tudo que não for letra ou número
  const cleaned = voucher.replace(/[^a-zA-Z0-9]/g, "");
  // Formatação dinâmica conforme digitação
  let result = "";
  if (cleaned.length <= 3) {
    result = cleaned;
  } else if (cleaned.length <= 7) {
    result = `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  } else if (cleaned.length <= 10) {
    result = `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
  } else {
    // Limita a 10 caracteres
    result = `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7, 10)}`;
  }
  return result;
}

// Funções auxiliares internas
function isValidCPF(strTemp: string) {
  if (!strTemp) {
    return false;
  }
  var cpfmf, Multiplic, soma, Count, cchar, cpeso, resto, digito;
  strTemp = strTemp.replaceAll(" ", "");
  if (
    strTemp === "00000000000" ||
    strTemp === "11111111111" ||
    strTemp === "22222222222" ||
    strTemp === "33333333333" ||
    strTemp === "44444444444" ||
    strTemp === "55555555555" ||
    strTemp === "66666666666" ||
    strTemp === "77777777777" ||
    strTemp === "88888888888" ||
    strTemp === "99999999999"
  )
    return false;
  cpfmf = strTemp.trim();
  Multiplic = "1110 9 8 7 6 5 4 3 2";
  soma = 0;
  for (Count = 0; Count < 9; Count++) {
    cchar = cpfmf.substring(8 - Count, 8 - Count + 1);
    cpeso = Multiplic.substring(18 - Count * 2, 18 - Count * 2 + 2);
    soma = soma + parseInt(cchar) * parseInt(cpeso);
  }
  resto = soma % 11;
  if (resto === 0) digito = 0;
  else {
    digito = 11 - resto;
    if (digito > 9) digito = digito - 10;
  }
  if (parseInt(cpfmf.substring(9, 10)) !== digito) return false;
  soma = 0;
  for (Count = 0; Count < 10; Count++) {
    cchar = cpfmf.substring(9 - Count, 9 - Count + 1);
    cpeso = Multiplic.substring(18 - Count * 2, 18 - Count * 2 + 2);
    soma = soma + parseInt(cchar) * parseInt(cpeso);
  }
  resto = soma % 11;
  if (resto === 0) digito = 0;
  else {
    digito = 11 - resto;
    if (digito > 9) digito = digito - 10;
  }
  if (cpfmf.substring(cpfmf.length - 1, cpfmf.length) !== digito.toString())
    return false;
  return true;
}

// function isValidCNPJ(strTemp: string) {
//   if (!strTemp) {
//     return false;
//   }
//   var i, prod, d1, d2, p, dig, mult; //06.182.795/0001-82
//   strTemp = strTemp.replaceAll(" ", "");
//   if (strTemp.length < 14 || strTemp === "00000000000000") return false;
//   p = strTemp.trim();
//   mult = "543298765432";
//   dig = "*" + p;
//   for (prod = 0, i = 1; i <= 12; i++) {
//     prod += parseInt(dig.charAt(i)) * (parseInt(mult.charAt(i - 1)) - 0);
//   }
//   if ((d1 = prod % 11) < 2) {
//     d1 = 0;
//   } else {
//     d1 = 11 - d1;
//   }
//   if (d1 !== parseInt(dig.charAt(13))) {
//     return false;
//   } else {
//   }
//   mult = "6543298765432";
//   for (prod = 0, i = 1; i <= 13; i++) {
//     prod += parseInt(dig.charAt(i)) * (parseInt(mult.charAt(i - 1)) - 0);
//   }
//   if ((d2 = prod % 11) < 2) {
//     d2 = 0;
//   } else {
//     d2 = 11 - d2;
//   }
//   if (d2 !== parseInt(dig.charAt(14))) {
//     return false;
//   } else {
//   }
//   return true;
// }

function validateCPF(cpf: string) {
  // 1. Remove formatação, mantendo letras e números
  const cleanCPF = cpf.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

  // 2. Validação básica de tamanho e sequências repetidas
  if (cleanCPF.length !== 11 || /^(\w)\1{10}$/.test(cleanCPF)) {
    return false;
  }

  let sum, remainder, i;

  // --- CÁLCULO DO PRIMEIRO DÍGITO ---
  sum = 0;
  for (i = 1; i <= 9; i++) {
    // REGRA ALFANUMÉRICA: Valor ASCII - 48
    // Se for 'A', vira 17. Se for '0', vira 0.
    let charValue = cleanCPF.charCodeAt(i - 1) - 48;
    sum += charValue * (11 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;

  // O 10º caractere deve ser o primeiro dígito verificador (sempre número)
  if (remainder !== parseInt(cleanCPF.substring(9, 10))) return false;

  // --- CÁLCULO DO SEGUNDO DÍGITO ---
  sum = 0;
  for (i = 1; i <= 10; i++) {
    let charValue = cleanCPF.charCodeAt(i - 1) - 48;
    sum += charValue * (12 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;

  // O 11º caractere deve ser o segundo dígito verificador (sempre número)
  if (remainder !== parseInt(cleanCPF.substring(10, 11))) return false;

  return true;
}

function isValidCNPJ(strTemp: string) {
  if (!strTemp) return false;

  // Limpa o que não for letra ou número
  strTemp = strTemp.replace(/[^a-zA-Z0-9]/g, "");

  if (strTemp.length !== 14 || strTemp === "00000000000000") return false;

  let prod, d1, d2, i;
  let mult1 = "543298765432";
  let mult2 = "6543298765432";

  // --- CÁLCULO DO PRIMEIRO DÍGITO (D1) ---
  prod = 0;
  for (i = 0; i < 12; i++) {
    // REGRA NOVA: Se for letra, pega o valor ASCII - 48
    let charValue = strTemp.charCodeAt(i) - 48;
    prod += charValue * parseInt(mult1.charAt(i));
  }

  d1 = prod % 11;
  d1 = d1 < 2 ? 0 : 11 - d1;

  // O 13º caractere (índice 12) é sempre numérico
  if (d1 !== parseInt(strTemp.charAt(12))) return false;

  // --- CÁLCULO DO SEGUNDO DÍGITO (D2) ---
  prod = 0;
  for (i = 0; i < 13; i++) {
    // Mesma regra: ASCII - 48
    let charValue = strTemp.charCodeAt(i) - 48;
    prod += charValue * parseInt(mult2.charAt(i));
  }

  d2 = prod % 11;
  d2 = d2 < 2 ? 0 : 11 - d2;

  // O 14º caractere (índice 13) é sempre numérico
  if (d2 !== parseInt(strTemp.charAt(13))) return false;

  return true;
}

export {
  ufs,
  daysOfWeek,
  validateEmail,
  formatMoney,
  onlyNumbers,
  formatPercent,
  getMonth,
  getPercent,
  validateCPF,
  formatDocument,
  formatDocumentType,
  formatVoucher,
  formatData,
  formatCEP,
  formatPhone,
  formatTrim,
  formatUpper,
  formatLower,
  formatNumber,
  completeDoc,
  getPhoneWithCountryCode,
  removeTagHTML,
  validateDocument,
  validNumberPhone,
  isCelular,
};
